import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { ApiError } from "../lib/api.js";
import {
  deleteIncomeEvent,
  listIncomeEvents,
} from "../lib/api-modules/income-events.js";
import { listEquityGrants } from "../lib/api-modules/equity-grants.js";
import {
  deleteIncomeSchedule,
  listIncomeSchedules,
  updateIncomeSchedule,
} from "../lib/api-modules/income-schedules.js";
import {
  INCOME_EVENT_TYPE_LABELS,
  INCOME_SCHEDULE_TYPE_LABELS,
  PAYCHECK_FREQUENCY_LABELS,
  type IncomeEvent,
  type IncomeEventType,
  type IncomeSchedule,
  type MembershipRole,
} from "../lib/api-modules/types.js";
import { formatCentsCurrency } from "../lib/util/money.js";
import { nextPayDates } from "../lib/util/next-pay-dates.js";
import { ErrorMessage } from "../components/auth/error-message.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Toast, useToast } from "../components/toast.js";
import { IncomeEventForm } from "../components/income/IncomeEventForm.js";
import { IncomeScheduleForm } from "../components/income/IncomeScheduleForm.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";

const INCOME_PROMPTS = [
  "When's my next paycheck?",
  "What does my next paycheck look like?",
  "What's my YTD 401k contribution?",
  "How much have we earned this year?",
  "What's my estimated tax setaside per invoice?",
  "Show me an everything snapshot.",
];

/**
 * /income — household income events.
 *
 * Three views via the type-filter dropdown:
 *   - All (default)
 *   - Salary / bonus / paychecks
 *   - Equity (RSU vest, ISO/NSO/ESPP)
 *   - Side income
 *
 * Server-side `?event_type=` narrows the result; the page also exposes
 * a YTD total + a by-event-type breakdown + a payer rollup widget.
 * Mark-archive is the soft-delete (idempotent on already-archived).
 */

const READ_ONLY_ROLES: ReadonlySet<MembershipRole> = new Set([
  "viewer",
  "auditor",
]);

interface FilterState {
  readonly eventType: IncomeEventType | "all";
  readonly view: "year" | "all";
}

type ActiveTab = "events" | "schedules";

export function IncomePage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>("events");
  if (!user) return null;
  const canWrite = !READ_ONLY_ROLES.has(user.role);

  return (
    <div className="mx-auto max-w-5xl p-6">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Income</h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            Paychecks, RSU vests, ESPP purchases, side income — your
            day-job + alt income ledger. Set up a recurring schedule on
            the Schedules tab so you stop entering paychecks one-by-one.
          </p>
        </div>
      </header>

      {!canWrite && (
        <p className="mb-6 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
          Read-only access — ask an owner or admin to make changes.
        </p>
      )}

      <div
        role="tablist"
        aria-label="Income tabs"
        className="mb-6 flex gap-1 border-b border-zinc-200 dark:border-zinc-800"
      >
        {(["events", "schedules"] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={activeTab === t}
            onClick={() => setActiveTab(t)}
            className={`-mb-px px-4 py-2 text-sm font-medium transition ${
              activeTab === t
                ? "border-b-2 border-brand-500 text-brand-700 dark:text-brand-300"
                : "text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200"
            }`}
          >
            {t === "events" ? "Events" : "Schedules"}
          </button>
        ))}
      </div>

      {activeTab === "events" && <IncomeBody canWrite={canWrite} />}
      {activeTab === "schedules" && <SchedulesBody canWrite={canWrite} />}

      <div className="mt-8">
        <SuggestedPromptsTile prompts={INCOME_PROMPTS} />
      </div>
    </div>
  );
}

function SchedulesBody({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  const schedulesQuery = useQuery({
    queryKey: ["income-schedules", includeInactive],
    queryFn: () => listIncomeSchedules({ includeInactive }),
  });

  const deleteM = useMutation({
    mutationFn: deleteIncomeSchedule,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["income-schedules"] });
      toast.show("Schedule deleted");
    },
    onError: (e) => toast.show((e).message),
  });
  const pauseM = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      updateIncomeSchedule(id, { is_active: active }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["income-schedules"] });
      toast.show("Schedule updated");
    },
    onError: (e) => toast.show((e).message),
  });

  if (schedulesQuery.isLoading) {
    return <p className="text-sm text-zinc-500">Loading schedules…</p>;
  }
  if (schedulesQuery.error) {
    return (
      <ErrorMessage>
        {schedulesQuery.error instanceof ApiError
          ? schedulesQuery.error.message
          : "Couldn't load schedules."}
      </ErrorMessage>
    );
  }

  const schedules = schedulesQuery.data?.income_schedules ?? [];

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-400">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Include paused / sunset schedules
        </label>
        {canWrite && !showCreate && editingId === null && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600"
          >
            Add schedule
          </button>
        )}
      </div>

      {showCreate && (
        <IncomeScheduleForm
          mode="create"
          onClose={() => setShowCreate(false)}
        />
      )}

      {schedules.length === 0 ? (
        <p className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700">
          No schedules yet.{" "}
          {canWrite ? 'Click "Add schedule" to set up a recurring paycheck.' : ""}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {schedules.map((s) =>
            editingId === s.schedule_id ? (
              <li key={s.schedule_id}>
                <IncomeScheduleForm
                  mode="edit"
                  initial={s}
                  onClose={() => setEditingId(null)}
                />
              </li>
            ) : (
              <ScheduleCard
                key={s.schedule_id}
                schedule={s}
                canWrite={canWrite}
                onEdit={() => setEditingId(s.schedule_id)}
                onPauseToggle={() =>
                  pauseM.mutate({
                    id: s.schedule_id,
                    active: !s.is_active,
                  })
                }
                onDelete={() => deleteM.mutate(s.schedule_id)}
                isMutating={deleteM.isPending || pauseM.isPending}
              />
            ),
          )}
        </ul>
      )}

      <Toast message={toast.message} />
    </section>
  );
}

function ScheduleCard({
  schedule,
  canWrite,
  onEdit,
  onPauseToggle,
  onDelete,
  isMutating,
}: {
  schedule: IncomeSchedule;
  canWrite: boolean;
  onEdit: () => void;
  onPauseToggle: () => void;
  onDelete: () => void;
  isMutating: boolean;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const through = new Date();
  through.setUTCDate(through.getUTCDate() + 60);
  const next4 = nextPayDates(
    schedule,
    today,
    through.toISOString().slice(0, 10),
  ).slice(0, 4);
  const annualPretty =
    schedule.annual_gross_cents !== undefined
      ? formatCentsCurrency(schedule.annual_gross_cents)
      : schedule.per_pay_gross_cents !== undefined
        ? `${formatCentsCurrency(schedule.per_pay_gross_cents)} / pay`
        : null;

  return (
    <li className="rounded-md border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-base font-medium">{schedule.payer}</h3>
            <span className="rounded-full bg-zinc-200 px-2 py-0.5 text-[10px] font-normal text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
              {INCOME_SCHEDULE_TYPE_LABELS[schedule.schedule_type]}
            </span>
            {schedule.frequency && (
              <span className="rounded-full bg-zinc-200 px-2 py-0.5 text-[10px] font-normal text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                {PAYCHECK_FREQUENCY_LABELS[schedule.frequency]}
              </span>
            )}
            {!schedule.is_active && (
              <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-normal text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                paused
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
            {annualPretty && (
              <>
                <span className="font-medium text-zinc-700 dark:text-zinc-300">
                  {annualPretty}
                </span>
                {" · "}
              </>
            )}
            effective {schedule.effective_from}
            {schedule.end_date && <> · ends {schedule.end_date}</>}
          </p>
          {next4.length > 0 && (
            <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
              Next paychecks:{" "}
              <span className="font-mono">{next4.join(" · ")}</span>
            </p>
          )}
          {schedule.estimated_tax_pct !== undefined && (
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
              Suggested setaside per invoice:{" "}
              <span className="font-medium">
                {schedule.estimated_tax_pct}%
              </span>
            </p>
          )}
          {schedule.notes && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-700 dark:text-zinc-300">
              {schedule.notes}
            </p>
          )}
        </div>
        {canWrite && (
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={onEdit}
              disabled={isMutating}
              className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 disabled:opacity-60 dark:border-zinc-700 dark:hover:bg-zinc-900"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onPauseToggle}
              disabled={isMutating}
              className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 disabled:opacity-60 dark:border-zinc-700 dark:hover:bg-zinc-900"
            >
              {schedule.is_active ? "Pause" : "Resume"}
            </button>
            <ConfirmButton
              label="Delete"
              pendingLabel="Deleting…"
              size="xs"
              ariaLabel={`Delete ${schedule.payer} schedule`}
              onConfirm={onDelete}
              isPending={isMutating}
            />
          </div>
        )}
      </div>
    </li>
  );
}

function IncomeBody({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState<FilterState>({
    eventType: "all",
    view: "year",
  });
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const todayY = new Date().getUTCFullYear();
  const yearFrom = `${todayY}-01-01`;
  const yearTo = `${todayY}-12-31`;

  const eventsQuery = useQuery({
    queryKey: ["income-events", filter],
    queryFn: () =>
      listIncomeEvents({
        ...(filter.view === "year"
          ? { from: yearFrom, to: yearTo }
          : {}),
        ...(filter.eventType !== "all"
          ? { eventType: filter.eventType }
          : {}),
      }),
  });

  const grantsQuery = useQuery({
    queryKey: ["equity-grants", false],
    queryFn: () => listEquityGrants(false),
  });

  const archiveMutation = useMutation({
    mutationFn: ({ date, eventId }: { date: string; eventId: string }) =>
      deleteIncomeEvent(date, eventId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["income-events"] });
      toast.show("Income event archived");
    },
    onError: () => toast.show("Couldn't archive"),
  });

  const events = eventsQuery.data?.income_events ?? [];
  const grants = grantsQuery.data?.equity_grants ?? [];

  const ytdTotal = useMemo(
    () => events.reduce((s, e) => s + e.amount_cents, 0),
    [events],
  );
  const byType = useMemo(() => byEventType(events), [events]);
  const byPayer = useMemo(() => byPayerRollup(events), [events]);
  const sideIncomeTotal = byType.get("side_income") ?? 0;

  if (eventsQuery.isLoading) {
    return (
      <p className="text-sm text-zinc-500" role="status">
        Loading income…
      </p>
    );
  }
  if (eventsQuery.error) {
    return (
      <ErrorMessage>
        {eventsQuery.error instanceof ApiError
          ? eventsQuery.error.message
          : "Couldn't load income events."}
      </ErrorMessage>
    );
  }

  return (
    <section>
      {/* Headline */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Headline label={filter.view === "year" ? `${todayY} total` : "All-time total"} value={ytdTotal} />
        <Headline label="Side income (in view)" value={sideIncomeTotal} muted />
        <Headline label="Events" value={events.length} count />
      </div>

      {/* Filter + add */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs">
            Period
            <select
              value={filter.view}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  view: e.target.value as FilterState["view"],
                }))
              }
              className="ml-2 rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-950"
            >
              <option value="year">This year</option>
              <option value="all">All time</option>
            </select>
          </label>
          <label className="text-xs">
            Type
            <select
              value={filter.eventType}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  eventType: e.target.value as FilterState["eventType"],
                }))
              }
              className="ml-2 rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-950"
            >
              <option value="all">All</option>
              {(
                Object.keys(INCOME_EVENT_TYPE_LABELS) as IncomeEventType[]
              ).map((t) => (
                <option key={t} value={t}>
                  {INCOME_EVENT_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
        </div>
        {canWrite && !showCreate && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600"
          >
            Add income
          </button>
        )}
      </div>

      {showCreate && (
        <div className="mb-4">
          <IncomeEventForm
            mode="create"
            equityGrants={grants}
            onClose={() => setShowCreate(false)}
          />
        </div>
      )}

      {/* By-type breakdown */}
      {events.length > 0 && (
        <div className="mb-6 rounded-md border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
          <h2 className="mb-3 text-sm font-semibold">By event type</h2>
          <ul className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
            {Array.from(byType.entries())
              .sort((a, b) => b[1] - a[1])
              .map(([t, sum]) => (
                <li
                  key={t}
                  className="flex items-center justify-between border-b border-zinc-100 py-1 dark:border-zinc-900"
                >
                  <span>{INCOME_EVENT_TYPE_LABELS[t]}</span>
                  <span className="font-medium">{formatCentsCurrency(sum)}</span>
                </li>
              ))}
          </ul>
        </div>
      )}

      {/* By-payer rollup */}
      {byPayer.size > 0 && (
        <div className="mb-6 rounded-md border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
          <h2 className="mb-3 text-sm font-semibold">By payer</h2>
          <ul className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
            {Array.from(byPayer.entries())
              .sort((a, b) => b[1] - a[1])
              .slice(0, 10)
              .map(([p, sum]) => (
                <li
                  key={p}
                  className="flex items-center justify-between border-b border-zinc-100 py-1 dark:border-zinc-900"
                >
                  <span className="truncate pr-2">{p}</span>
                  <span className="font-medium">{formatCentsCurrency(sum)}</span>
                </li>
              ))}
          </ul>
        </div>
      )}

      {/* Event list */}
      {events.length === 0 ? (
        <p className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700">
          No income events in this view. {canWrite ? 'Click "Add income" to start.' : ""}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {[...events]
            .sort((a, b) => (a.date < b.date ? 1 : -1))
            .map((e) =>
              editingId === e.event_id ? (
                <li key={e.event_id}>
                  <IncomeEventForm
                    mode="edit"
                    event={e}
                    equityGrants={grants}
                    onClose={() => setEditingId(null)}
                  />
                </li>
              ) : (
                <IncomeEventCard
                  key={e.event_id}
                  event={e}
                  canWrite={canWrite}
                  onEdit={() => setEditingId(e.event_id)}
                  onArchive={() =>
                    archiveMutation.mutate({
                      date: e.date,
                      eventId: e.event_id,
                    })
                  }
                  isMutating={archiveMutation.isPending}
                />
              ),
            )}
        </ul>
      )}

      <Toast message={toast.message} />
    </section>
  );
}

function Headline({
  label,
  value,
  muted,
  count,
}: {
  label: string;
  value: number;
  muted?: boolean;
  count?: boolean;
}) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
      <div className="text-xs text-zinc-500">{label}</div>
      <div
        className={`mt-1 text-xl font-semibold ${
          muted ? "text-zinc-700 dark:text-zinc-300" : ""
        }`}
      >
        {count ? value.toLocaleString() : formatCentsCurrency(value)}
      </div>
    </div>
  );
}

function IncomeEventCard({
  event,
  canWrite,
  onEdit,
  onArchive,
  isMutating,
}: {
  event: IncomeEvent;
  canWrite: boolean;
  onEdit: () => void;
  onArchive: () => void;
  isMutating: boolean;
}) {
  return (
    <li className="rounded-md border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-base font-medium">{event.payer}</h3>
            <span className="rounded-full bg-zinc-200 px-2 py-0.5 text-[10px] font-normal text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
              {INCOME_EVENT_TYPE_LABELS[event.event_type]}
            </span>
            {event.is_archived && (
              <span className="rounded-full bg-zinc-200 px-2 py-0.5 text-[10px] font-normal text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                archived
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
            <span className="font-medium text-zinc-700 dark:text-zinc-300">
              {formatCentsCurrency(event.amount_cents)}
            </span>{" "}
            · <time dateTime={event.date}>{event.date}</time>
            {event.currency !== "USD" && <> · {event.currency}</>}
            {event.gross_cents !== undefined && (
              <>
                {" "}
                · gross {formatCentsCurrency(event.gross_cents)}
              </>
            )}
            {event.withholding_cents !== undefined && (
              <>
                {" "}
                · w/h {formatCentsCurrency(event.withholding_cents)}
              </>
            )}
          </p>
          {event.notes && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-700 dark:text-zinc-300">
              {event.notes}
            </p>
          )}
        </div>
        {canWrite && !event.is_archived && (
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={onEdit}
              disabled={isMutating}
              className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 disabled:opacity-60 dark:border-zinc-700 dark:hover:bg-zinc-900"
            >
              Edit
            </button>
            <ConfirmButton
              label="Archive"
              pendingLabel="Archiving…"
              size="xs"
              ariaLabel={`Archive ${event.payer}`}
              onConfirm={onArchive}
              isPending={isMutating}
            />
          </div>
        )}
      </div>
    </li>
  );
}

function byEventType(events: ReadonlyArray<IncomeEvent>): Map<IncomeEventType, number> {
  const m = new Map<IncomeEventType, number>();
  for (const e of events) {
    if (e.is_archived) continue;
    m.set(e.event_type, (m.get(e.event_type) ?? 0) + e.amount_cents);
  }
  return m;
}

function byPayerRollup(events: ReadonlyArray<IncomeEvent>): Map<string, number> {
  const m = new Map<string, number>();
  for (const e of events) {
    if (e.is_archived) continue;
    m.set(e.payer, (m.get(e.payer) ?? 0) + e.amount_cents);
  }
  return m;
}
